/**
 * Contact Address Book (Phase 1) — "Select Recipients" modal for the Send page.
 *
 * Views: Sendlinx Contacts, Microsoft Contacts, Google Contacts, iCloud Contacts, Yahoo Contacts, Recent Recipients, Favourites, Groups, Import Contacts, Manage Contacts.
 * Microsoft Contacts (Microsoft365View) lists the user's Outlook contacts (Microsoft 365, Outlook.com,
 * Hotmail, Live) read-only; Google Contacts (GoogleContactsView) lists contacts synced read-only from
 * Gmail / Google Workspace; iCloud Contacts (ICloudContactsView) lists contacts synced read-only from
 * Apple iCloud over CardDAV with an app-specific password; Yahoo Contacts (YahooContactsView) lists
 * contacts synced read-only from Yahoo / Ymail / RocketMail / AOL (OAuth + CardDAV). Their rows join the same selection.
 * Selection is kept across views and searches; "Add Recipients" hands back a
 * case-insensitive, de-duplicated list of emails. The caller merges them into
 * its own recipient list, so typed recipients, message and files are untouched.
 * Picking a contact never authorizes it under FAR: the Send page re-evaluates every address.
 *
 * "Add to Contacts" (Recent Recipients guide) opens the Review & Save Contacts
 * step for the current selection: names, groups and Favourites are saved to the
 * address book only. The selection is kept and nothing is added to the transfer.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@material-ui/core/Dialog";
import withStyles from "@material-ui/core/styles/withStyles";
import CloseIcon from "@material-ui/icons/Close";
import Star from "@material-ui/icons/Star";
import StarBorder from "@material-ui/icons/StarBorder";
import ContactsOutlined from "@material-ui/icons/ContactsOutlined";
import History from "@material-ui/icons/History";
import GroupWorkOutlined from "@material-ui/icons/GroupWorkOutlined";
import PublishOutlined from "@material-ui/icons/PublishOutlined";
import SettingsOutlined from "@material-ui/icons/SettingsOutlined";
import EditOutlined from "@material-ui/icons/EditOutlined";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ChevronRight from "@material-ui/icons/ChevronRight";
import PersonAddOutlined from "@material-ui/icons/PersonAddOutlined";
import PermContactCalendarOutlined from "@material-ui/icons/PermContactCalendarOutlined";
import ContactMailOutlined from "@material-ui/icons/ContactMailOutlined";
import CloudOutlined from "@material-ui/icons/CloudOutlined";
import ExtensionOutlined from "@material-ui/icons/ExtensionOutlined";
import ContactsApi from "../../../../assets/js/utils/contacts/contactsApi";
import { parseCsv, guessCsvMapping, csvToRecords, parseVcard, classifyRecords, isValidEmail } from "../../../../assets/js/utils/contacts/contactImport";
import addressBookStyle from "./addressBookStyle.jsx";
import ReviewSaveContacts, { NAME_MAX, COMPANY_MAX } from "./ReviewSaveContacts.jsx";
import Microsoft365View from "./Microsoft365View.jsx";
import GoogleContactsView from "./GoogleContactsView.jsx";
import ICloudContactsView from "./ICloudContactsView.jsx";
import YahooContactsView, { YahooMark } from "./YahooContactsView.jsx";
import { getAddressBookAccess, THIRD_PARTY_VIEWS, EXTENSIONS_PATH } from "../../../../assets/js/utils/contacts/addressBookAccess";

const VIEWS = [
    { key: "contacts", label: "Sendlinx Contacts", icon: ContactsOutlined },
    { key: "microsoft", label: "Microsoft Contacts", icon: PermContactCalendarOutlined },
    { key: "google", label: "Google Contacts", icon: ContactMailOutlined },
    { key: "icloud", label: "iCloud Contacts", icon: CloudOutlined },
    { key: "yahoo", label: "Yahoo Contacts", icon: YahooNavIcon, accent: "yahoo" },
    { key: "recent", label: "Recent Recipients", icon: History },
    { key: "favourites", label: "Favourites", icon: StarBorder },
    { key: "groups", label: "Groups", icon: GroupWorkOutlined },
    { divider: true },
    { key: "import", label: "Import Contacts", icon: PublishOutlined },
    { key: "manage", label: "Manage Contacts", icon: SettingsOutlined }
];
// Shown instead of the four third-party address books while the Address Book extension is off.
const MORE_VIEW = { key: "more", label: "More Address Books", icon: ExtensionOutlined };
const THIRD_PARTY_LIST = [
    { label: "Microsoft 365 / Outlook", icon: PermContactCalendarOutlined },
    { label: "Google / Gmail", icon: ContactMailOutlined },
    { label: "iCloud", icon: CloudOutlined },
    { label: "Yahoo, Ymail, RocketMail & AOL", icon: YahooNavIcon }
];
/** Sidebar icon for Yahoo Contacts: the "Y!" mark sized like the Material icons. */
function YahooNavIcon(){
    return <YahooMark className="sl-yahoo-nav-mark" />;
}
const lower = (v) => String(v || "").toLowerCase();
const initials = (c) => {
    const src = (c.name || c.email || "?").replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/);
    return ((src[0] || "?")[0] + (src.length > 1 ? src[src.length - 1][0] : "")).toUpperCase();
};
const formatDate = (value) => {
    if(!value){ return ""; }
    const d = new Date(String(value).replace(" ", "T"));
    return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const errorText = (err, fallback) => (err && err.message && err.message !== "Request Cancelled" ? err.message : fallback);
const RECENT_PAGE = 50;
const RECENT_STATUS = [["all", "All Recipients"], ["not", "Not in Contacts"], ["in", "In Contacts"]];
const plural = (n, one, many) => n+" "+(n === 1 ? one : (many || one+"s"));
/** One-line result of Save Contacts for the success banner. */
const saveSummaryText = (s) => {
    const parts = [];
    parts.push(plural(s.createdCount || 0, "contact")+" created");
    if(s.existingCount){ parts.push(s.existingCount+" already in contacts"+(s.updatedCount ? " ("+s.updatedCount+" updated)" : "")); }
    if(s.groupCreated){ parts.push("group \""+s.groupCreated.name+"\" created"); }
    if(s.membershipsAdded){ parts.push(plural(s.membershipsAdded, "group membership")+" added"); }
    if(s.favouritesAdded){ parts.push(s.favouritesAdded+" added to Favourites"); }
    return "Contacts saved: "+parts.join(" · ")+". Your selection is unchanged.";
};

function AddressBookModal({ classes, open, onClose, onAdd, existingRecipients, initialView, user, onOpenExtensions }){
    const access = getAddressBookAccess(user);
    const thirdParty = access.enabled;
    const [view, setView] = useState(!thirdParty && THIRD_PARTY_VIEWS.includes(initialView) ? "more" : initialView);
    const navViews = thirdParty ? VIEWS : VIEWS.reduce((list, v) => {
        if(v.key === "microsoft"){ list.push(MORE_VIEW); }
        if(!THIRD_PARTY_VIEWS.includes(v.key)){ list.push(v); }
        return list;
    }, []);
    const [contacts, setContacts] = useState([]);
    const [groups, setGroups] = useState([]);
    // Recent Recipients are paged and searched on the server.
    const [recent, setRecent] = useState(null);           // loaded rows (all pages so far) or null
    const [recentMeta, setRecentMeta] = useState({ total: 0, page: 0, hasMore: false, loading: false });
    const [recentStatus, setRecentStatus] = useState("all");
    const [recentSort, setRecentSort] = useState({ key: "lastUsed", dir: -1 });
    const [reviewing, setReviewing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [search, setSearch] = useState("");
    const [groupFilter, setGroupFilter] = useState("all");
    const [sort, setSort] = useState({ key: "name", dir: 1 });
    const [selected, setSelected] = useState([]); // [{ email, name }]
    const [expanded, setExpanded] = useState({});
    const titleRef = useRef(null);
    const addContactsRef = useRef(null);
    const recentRequest = useRef(0);

    const load = () => {
        setLoading(true);
        setError(null);
        return ContactsApi.list().then(data => {
            setContacts(data.contacts || []);
            setGroups(data.groups || []);
            setLoading(false);
            return data.contacts || [];
        }).catch(err => {
            setError(errorText(err, "Your contacts could not be loaded."));
            setLoading(false);
            return null;
        });
    };
    useEffect(() => { if(open){ load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    const loadRecent = (page = 1) => {
        const id = ++recentRequest.current;
        setRecentMeta(m => ({ ...m, loading: true }));
        return ContactsApi.recent({ search: search.trim(), status: recentStatus, sort: recentSort.key,
            dir: recentSort.dir === 1 ? "asc" : "desc", page, limit: RECENT_PAGE }).then(data => {
            if(id !== recentRequest.current){ return; }   // a newer search replaced this one
            const rows = data.recent || [];
            setRecent(prev => (page === 1 || !prev ? rows : prev.concat(rows.filter(r => !prev.some(p => p.email === r.email)))));
            setRecentMeta({ total: data.total !== undefined ? data.total : rows.length, page, hasMore: !!data.hasMore, loading: false });
        }).catch(err => {
            if(id !== recentRequest.current){ return; }
            setRecent(prev => prev || []);
            setRecentMeta(m => ({ ...m, loading: false }));
            setError(errorText(err, "Recent recipients could not be loaded."));
        });
    };
    // Server-side search: debounce typing; filter and sort changes reload at once.
    useEffect(() => {
        if(!open || view !== "recent"){ return undefined; }
        const timer = setTimeout(() => loadRecent(1), recent === null ? 0 : 300);
        return () => clearTimeout(timer);
    }, [open, view, search, recentStatus, recentSort]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setSearch(""); setNotice(null); }, [view]);

    const already = useMemo(() => new Set((existingRecipients || []).map(lower)), [existingRecipients]);
    const selectedSet = useMemo(() => new Set(selected.map(s => s.email)), [selected]);
    const isSelected = (email) => selectedSet.has(lower(email));
    const toggle = (item, on) => {
        const email = lower(item.email);
        setSelected(prev => {
            const has = prev.some(s => s.email === email);
            const want = on === undefined ? !has : on;
            if(want && !has){ return prev.concat([{ email, name: item.name || "" }]); }
            if(!want && has){ return prev.filter(s => s.email !== email); }
            return prev;
        });
    };
    const toggleMany = (items, on) => items.forEach(i => toggle(i, on));

    const matches = (c) => {
        const q = lower(search).trim();
        return !q || lower(c.name).includes(q) || lower(c.email).includes(q) || lower(c.company).includes(q);
    };
    const sortRows = (rows) => rows.slice().sort((a, b) => {
        const av = sort.key === "group" ? lower((a.groups[0] || {}).name) : sort.key === "lastUsed" ? String(a.lastUsed || "") : lower(a[sort.key] || a.email);
        const bv = sort.key === "group" ? lower((b.groups[0] || {}).name) : sort.key === "lastUsed" ? String(b.lastUsed || "") : lower(b[sort.key] || b.email);
        return av < bv ? -sort.dir : av > bv ? sort.dir : 0;
    });
    const filteredContacts = (onlyFavourites) => sortRows(contacts.filter(c =>
        matches(c) && (!onlyFavourites || c.favourite) &&
        (groupFilter === "all" || c.groups.some(g => String(g.id) === String(groupFilter)))
    ));

    const setFavourite = (contact) => {
        const next = !contact.favourite;
        setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, favourite: next } : c));
        ContactsApi.favouriteContact(contact.id, next).catch(err => {
            setContacts(prev => prev.map(c => c.id === contact.id ? { ...c, favourite: !next } : c));
            setError(errorText(err, "The favourite could not be saved."));
        });
    };
    const setGroupFavourite = (group) => {
        const next = !group.favourite;
        setGroups(prev => prev.map(g => g.id === group.id ? { ...g, favourite: next } : g));
        ContactsApi.favouriteGroup(group.id, next).catch(err => {
            setGroups(prev => prev.map(g => g.id === group.id ? { ...g, favourite: !next } : g));
            setError(errorText(err, "The favourite could not be saved."));
        });
    };
    const sortHeader = (key, label) => (
        <th scope="col" aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
            <button type="button" className={classes.sortButton} onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))}>
                {label}<span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
            </button>
        </th>
    );
    const selectAllCell = (rows, label) => {
        const all = rows.length > 0 && rows.every(r => isSelected(r.email));
        return (
            <th scope="col" style={{ width: 44 }}>
                <input type="checkbox" className={classes.checkbox} aria-label={label} checked={all} disabled={rows.length === 0}
                    onChange={(e) => toggleMany(rows, e.target.checked)} />
            </th>
        );
    };
    const rowCheckbox = (item) => (
        <td>
            <input type="checkbox" className={classes.checkbox} checked={isSelected(item.email)} onChange={() => toggle(item)}
                aria-label={"Select "+(item.name || item.email)} />
        </td>
    );
    const starButton = (on, onClick, label) => (
        <button type="button" className={classes.iconButton+" "+(on ? classes.starOn : classes.star)} onClick={onClick} aria-pressed={on} aria-label={label}>
            {on ? <Star /> : <StarBorder />}
        </button>
    );
    const nameCell = (item) => (
        <td>
            <span className={classes.nameCell}>
                <span className={classes.avatar} aria-hidden="true">{initials(item)}</span>
                <span>{item.name || <span className={classes.muted}>—</span>}{already.has(lower(item.email)) ? <span className={classes.muted}> (added)</span> : null}
                    {item.company ? <span className={classes.subLine}>{item.company}</span> : null}</span>
            </span>
        </td>
    );
    const searchBox = (placeholder, withGroup) => (
        <div className={classes.toolbar}>
            <input type="search" className={classes.search} placeholder={placeholder} aria-label={placeholder} value={search} onChange={(e) => setSearch(e.target.value)} />
            {withGroup ?
                <label className={classes.toolbar}>
                    <span>Group:</span>
                    <select className={classes.select} value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
                        <option value="all">All Groups</option>
                        {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                </label>
            : null}
        </div>
    );

    const renderContactTable = (rows, emptyText) => (
        <div className={classes.tableWrap}>
            <table className={classes.table}>
                <thead><tr>
                    {selectAllCell(rows, "Select all shown contacts")}
                    {sortHeader("name", "Name")}{sortHeader("email", "Email")}{sortHeader("group", "Group")}
                    <th scope="col" style={{ width: 52 }}><span className={classes.srOnly}>Favourite</span></th>
                </tr></thead>
                <tbody>
                    {rows.length === 0 ? <tr><td colSpan={5} className={classes.empty}>{emptyText}</td></tr> :
                        rows.map(c => (
                            <tr key={c.id} className={isSelected(c.email) ? classes.rowSelected : ""}>
                                {rowCheckbox(c)}{nameCell(c)}<td>{c.email}</td>
                                <td>{c.groups.map(g => g.name).join(", ") || <span className={classes.muted}>—</span>}</td>
                                <td>{starButton(c.favourite, () => setFavourite(c), (c.favourite ? "Remove " : "Add ")+(c.name || c.email)+" "+(c.favourite ? "from" : "to")+" favourites")}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );

    const recentSortHeader = (key, label) => (
        <th scope="col" aria-sort={recentSort.key === key ? (recentSort.dir === 1 ? "ascending" : "descending") : "none"}>
            <button type="button" className={classes.sortButton}
                onClick={() => setRecentSort(s => ({ key, dir: s.key === key ? -s.dir : (key === "lastUsed" ? -1 : 1) }))}>
                {label}<span aria-hidden="true">{recentSort.key === key ? (recentSort.dir === 1 ? "▲" : "▼") : "↕"}</span>
            </button>
        </th>
    );
    const renderRecent = () => {
        const rows = recent || [];
        const allShown = rows.length > 0 && rows.every(r => isSelected(r.email));
        const filtered = search.trim() !== "" || recentStatus !== "all";
        return (<>
            <div className={classes.toolbar}>
                <input type="search" className={classes.search} placeholder="Search recent recipients…" aria-label="Search recent recipients"
                    value={search} onChange={(e) => setSearch(e.target.value)} />
                <select className={classes.select} aria-label="Show" value={recentStatus} onChange={(e) => setRecentStatus(e.target.value)}>
                    {RECENT_STATUS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
            </div>
            <div className={classes.tableWrap} aria-busy={recentMeta.loading}>
                <table className={classes.table}>
                    <thead><tr>
                        {selectAllCell(rows, "Select all "+rows.length+" recent recipients shown")}
                        {recentSortHeader("name", "Name")}{recentSortHeader("email", "Email")}{recentSortHeader("lastUsed", "Last Used")}
                        <th scope="col">Status</th>
                    </tr></thead>
                    <tbody>
                        {recent === null ? <tr><td colSpan={5} className={classes.empty}>Loading recent recipients…</td></tr> :
                            rows.length === 0 ? <tr><td colSpan={5} className={classes.empty}>{filtered ? "No recent recipients match." : "No recent recipients yet. People you send files to will appear here."}</td></tr> :
                            rows.map(r => (
                                <tr key={r.email} className={isSelected(r.email) ? classes.rowSelected : ""}>
                                    {rowCheckbox(r)}{nameCell(r)}<td>{r.email}</td><td>{formatDate(r.lastUsed)}</td>
                                    <td>{r.inContacts || r.contactId
                                        ? <span className={classes.status+" "+classes.statusOk}>In Contacts</span>
                                        : <span className={classes.status+" "+classes.statusMuted}>Not in Contacts</span>}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            {recent !== null && rows.length > 0 ?
                <div className={classes.toolbar}>
                    <span className={classes.muted}>
                        Showing {rows.length} of {recentMeta.total}{filtered ? " matching" : ""}.
                        {allShown && recentMeta.hasMore ? " All shown are selected; load more to select the rest." : " Select all applies to the rows shown."}
                    </span>
                    {recentMeta.hasMore ?
                        <button type="button" className={classes.linkButton} disabled={recentMeta.loading} onClick={() => loadRecent(recentMeta.page + 1)}>
                            {recentMeta.loading ? "Loading…" : "Load more"}
                        </button>
                    : null}
                </div>
            : null}
        </>);
    };

    const renderGroups = (onlyFavourites) => {
        const rows = groups.filter(g => (!onlyFavourites || g.favourite) && (matches({ name: g.name, email: "" }) ||
            contacts.some(c => g.memberIds.includes(c.id) && matches(c))));
        if(onlyFavourites && rows.length === 0){ return null; }
        return (
            <div className={classes.tableWrap}>
                <table className={classes.table}>
                    <thead><tr><th scope="col" style={{ width: 44 }}><span className={classes.srOnly}>Select</span></th><th scope="col">{onlyFavourites ? "Favourite groups" : "Group"}</th><th scope="col">Members</th><th scope="col" style={{ width: 52 }}><span className={classes.srOnly}>Favourite</span></th></tr></thead>
                    <tbody>
                        {rows.length === 0 ? <tr><td colSpan={4} className={classes.empty}>No groups yet. Create one under Manage Contacts.</td></tr> :
                            rows.map(g => {
                                const members = contacts.filter(c => g.memberIds.includes(c.id));
                                const all = members.length > 0 && members.every(m => isSelected(m.email));
                                const isOpen = !!expanded[g.id];
                                return (
                                    <React.Fragment key={g.id}>
                                        <tr>
                                            <td><input type="checkbox" className={classes.checkbox} checked={all} disabled={members.length === 0}
                                                aria-label={"Select all members of "+g.name} onChange={(e) => toggleMany(members, e.target.checked)} /></td>
                                            <td>
                                                <button type="button" className={classes.sortButton} aria-expanded={isOpen} onClick={() => setExpanded(x => ({ ...x, [g.id]: !isOpen }))}>
                                                    {isOpen ? <ExpandMore /> : <ChevronRight />}<strong>{g.name}</strong>
                                                </button>
                                                {g.description ? <div className={classes.muted}>{g.description}</div> : null}
                                            </td>
                                            <td>{members.length}</td>
                                            <td>{starButton(g.favourite, () => setGroupFavourite(g), (g.favourite ? "Remove " : "Add ")+g.name+" "+(g.favourite ? "from" : "to")+" favourites")}</td>
                                        </tr>
                                        {isOpen ? members.map(m => (
                                            <tr key={g.id+"-"+m.id} className={classes.memberRow+" "+(isSelected(m.email) ? classes.rowSelected : "")}>
                                                {rowCheckbox(m)}<td>{m.name || m.email}</td><td colSpan={2}>{m.email}</td>
                                            </tr>
                                        )) : null}
                                    </React.Fragment>
                                );
                            })}
                    </tbody>
                </table>
            </div>
        );
    };

    const selectionView = view === "contacts" || view === "microsoft" || view === "google" || view === "icloud" || view === "yahoo" || view === "recent" || view === "favourites" || view === "groups";
    const submit = () => {
        const emails = [];
        const seen = new Set(already);
        selected.forEach(s => { if(isValidEmail(s.email) && !seen.has(s.email)){ seen.add(s.email); emails.push(s.email); } });
        onAdd(emails);
    };
    const openReview = () => {
        if(selected.length === 0){ return; }
        setNotice(null);
        setError(null);
        setReviewing(true);
    };
    const closeReview = () => {
        setReviewing(false);
        setTimeout(() => addContactsRef.current && addContactsRef.current.focus(), 0);
    };
    const onReviewSaved = (summary) => {
        if(summary && summary.groups){ setGroups(summary.groups); }
        setReviewing(false);
        setNotice({ text: saveSummaryText(summary || {}) });
        load().then(list => {
            // Show saved names on the chips; which addresses are selected does not change.
            if(list){
                const byEmail = {};
                list.forEach(c => { byEmail[lower(c.email)] = c; });
                setSelected(prev => prev.map(s => (byEmail[s.email] && byEmail[s.email].name ? { ...s, name: byEmail[s.email].name } : s)));
            }
            if(view === "recent"){ loadRecent(1); } else { setRecent(null); }
        });
        setTimeout(() => addContactsRef.current && addContactsRef.current.focus(), 0);
    };

    const openExtensions = () => {
        if(onOpenExtensions){ onOpenExtensions(); return; }
        onClose();
        try{ window.location.assign(EXTENSIONS_PATH); }catch(e){ }
    };
    const renderMore = () => (<>
        <div className={classes.msHeader}>
            <span className={classes.msIcon} aria-hidden="true"><ExtensionOutlined /></span>
            <div><h3 className={classes.msTitle}>More Address Books</h3><p className={classes.muted}>Microsoft 365, Google, iCloud & Yahoo</p></div>
        </div>
        <div className={classes.msPanel}>
            {!access.offered ? <>
                <p><strong>Third-party address books aren't available right now.</strong></p>
                <p className={classes.muted}>You can still type addresses or use your Sendlinx contacts, recent recipients, favourites and groups.</p>
            </> : <>
                <h4 className={classes.msConnectTitle}><ExtensionOutlined aria-hidden="true" />Turn on the Address Book extension</h4>
                <p className={classes.msLead}>Select recipients from the contacts you already keep in:</p>
                <ul className={classes.abMoreList}>
                    {THIRD_PARTY_LIST.map(item => <li key={item.label}><item.icon aria-hidden="true" />{item.label}</li>)}
                </ul>
                {access.canManage ? <>
                    <button type="button" className={classes.msConnectButton+" "+classes.abMoreButton} onClick={openExtensions}>
                        <ExtensionOutlined aria-hidden="true" />Turn on in Extensions
                    </button>
                    <p className={classes.muted}>Opens the Extensions page. Add any recipients you've already picked first.</p>
                </> :
                    <p className={classes.msWarn} role="status">Your administrator manages this for your organization. Ask them to turn on the Address Book extension.</p>}
            </>}
        </div>
    </>);

    const renderMain = () => {
        if(view === "more" || (!thirdParty && THIRD_PARTY_VIEWS.includes(view))){
            return renderMore();
        }
        // Microsoft Contacts load on their own; they don't wait for the Sendlinx address book.
        if(view === "microsoft"){
            return <Microsoft365View classes={classes} isSelected={isSelected} toggle={toggle} toggleMany={toggleMany} already={already} />;
        }
        if(view === "google"){
            return <GoogleContactsView classes={classes} isSelected={isSelected} toggle={toggle} toggleMany={toggleMany} already={already} />;
        }
        if(view === "icloud"){
            return <ICloudContactsView classes={classes} isSelected={isSelected} toggle={toggle} toggleMany={toggleMany} already={already} />;
        }
        if(view === "yahoo"){
            return <YahooContactsView classes={classes} isSelected={isSelected} toggle={toggle} toggleMany={toggleMany} already={already} />;
        }
        if(loading){ return <p className={classes.empty}>Loading contacts…</p>; }
        switch(view){
            case "recent": return renderRecent();
            case "favourites": return (<>
                {searchBox("Search favourites…", false)}
                {renderContactTable(filteredContacts(true), "No favourites yet. Star a contact to add it here.")}
                {renderGroups(true)}
            </>);
            case "groups": return (<>{searchBox("Search groups or members…", false)}{renderGroups(false)}</>);
            case "import": return <ImportPanel classes={classes} contacts={contacts} onDone={(summary) => {
                load();
                setNotice({ text: "Import finished: "+summary.added+" added, "+summary.skipped+" skipped, "+summary.failed+" failed"+(summary.groupsCreated ? ", "+summary.groupsCreated+" group(s) created" : "")+"." });
            }} />;
            case "manage": return <ManagePanel classes={classes} contacts={contacts} groups={groups} reload={load}
                onFavourite={setFavourite} onGroupFavourite={setGroupFavourite} goImport={() => setView("import")} />;
            default: return (<>
                {searchBox("Search contacts…", true)}
                {renderContactTable(filteredContacts(false), contacts.length === 0 ? "No contacts yet. Add one under Manage Contacts or import a CSV / vCard file." : "No contacts match your search.")}
            </>);
        }
    };

    return (
        <Dialog open={open} onClose={reviewing ? closeReview : onClose} classes={{ paper: classes.paper }} maxWidth={false} aria-labelledby="ab-title"
            onEntered={() => titleRef.current && titleRef.current.focus()}>
            <div className={classes.header}>
                <h2 className={classes.title} id="ab-title" tabIndex={-1} ref={titleRef}>{reviewing ? "Add to Contacts" : "Select Recipients"}</h2>
                <button type="button" className={classes.iconButton+" "+classes.close} onClick={reviewing ? closeReview : onClose}
                    aria-label={reviewing ? "Close review and go back to the recipient list" : "Close"}><CloseIcon /></button>
            </div>
            {reviewing ?
                <ReviewSaveContacts classes={classes} emails={selected.map(s => s.email)} groups={groups} onCancel={closeReview} onSaved={onReviewSaved} />
            : <>
            <div className={classes.layout}>
                <nav className={classes.nav} aria-label="Address book">
                    {navViews.map((v, i) => v.divider ? <div key={"d"+i} className={classes.navDivider} /> : (
                        <button key={v.key} type="button" className={classes.navItem+" "+(view === v.key ? classes.navActive+(v.accent === "yahoo" ? " "+classes.navActiveYahoo : "") : "")}
                            aria-current={view === v.key ? "page" : undefined} onClick={() => setView(v.key)}>
                            <v.icon aria-hidden="true" />{v.label}
                        </button>
                    ))}
                </nav>
                <div className={classes.main}>
                    {error ? <div className={classes.error} role="alert">{error}</div> : null}
                    {notice ? <div className={classes.success} role="status">{notice.text}</div> : null}
                    {renderMain()}
                </div>
            </div>
            <div className={classes.footer}>
                <div className={classes.chips} aria-label="Selected recipients">
                    {selected.map(s => (
                        <span key={s.email} className={classes.chip}>
                            {s.name || s.email}
                            <button type="button" className={classes.chipRemove} onClick={() => toggle(s, false)} aria-label={"Remove "+(s.name || s.email)}><CloseIcon /></button>
                        </span>
                    ))}
                </div>
                <span className={classes.count} aria-live="polite">{selected.length} recipient{selected.length === 1 ? "" : "s"} selected</span>
                <button type="button" className={classes.secondary} onClick={onClose}>Cancel</button>
                {selectionView || selected.length > 0 ? <>
                    <button type="button" ref={addContactsRef} className={classes.secondary} disabled={selected.length === 0} onClick={openReview}
                        title="Save the selected addresses to your contacts, groups or Favourites">
                        <PersonAddOutlined aria-hidden="true" />Add to Contacts
                    </button>
                    <button type="button" className={classes.primary} disabled={selected.length === 0} onClick={submit}>Add to Recipients{selected.length ? " ("+selected.length+")" : ""}</button>
                </> : null}
            </div>
            </>}
        </Dialog>
    );
}

/* ------------------------------ Import ------------------------------ */
function ImportPanel({ classes, contacts, onDone }){
    const [tab, setTab] = useState("csv");
    const [fileName, setFileName] = useState("");
    const [rows, setRows] = useState(null);       // raw CSV rows
    const [mapping, setMapping] = useState(null);
    const [vcards, setVcards] = useState(null);
    const [createGroups, setCreateGroups] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [drag, setDrag] = useState(false);
    const inputRef = useRef(null);

    const reset = () => { setRows(null); setMapping(null); setVcards(null); setFileName(""); setError(null); };
    const readFile = (file) => {
        if(!file){ return; }
        reset();
        const isVcf = /\.vcf$|\.vcard$/i.test(file.name);
        const isCsv = /\.csv$|\.txt$/i.test(file.name);
        if((tab === "csv" && !isCsv) || (tab === "vcard" && !isVcf)){
            setError(tab === "csv" ? "Choose a .csv file." : "Choose a .vcf file.");
            return;
        }
        if(file.size > 5 * 1024 * 1024){ setError("The file is larger than 5 MB."); return; }
        const reader = new FileReader();
        reader.onload = () => {
            setFileName(file.name);
            if(tab === "vcard"){
                const parsed = parseVcard(reader.result);
                if(parsed.length === 0){ setError("No contacts were found in this file."); return; }
                setVcards(parsed);
            }else{
                const parsed = parseCsv(reader.result);
                if(parsed.length === 0){ setError("The file is empty."); return; }
                setRows(parsed);
                setMapping(guessCsvMapping(parsed[0]));
            }
        };
        reader.onerror = () => setError("The file could not be read.");
        reader.readAsText(file, "UTF-8");
    };

    const records = useMemo(() => {
        const list = tab === "vcard" ? (vcards || []) : (rows && mapping ? csvToRecords(rows, mapping) : []);
        return classifyRecords(list, contacts.map(c => c.email));
    }, [tab, vcards, rows, mapping, contacts]);
    const importable = records.filter(r => r.status === "ok" || (r.status === "existing" && r.group));
    const counts = records.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
    const hasGroups = records.some(r => r.group);

    const confirm = () => {
        setBusy(true);
        setError(null);
        ContactsApi.importContacts(importable.map(r => ({ name: r.name, email: r.email, group: r.group })), createGroups).then(summary => {
            setBusy(false);
            reset();
            onDone(summary);
        }).catch(err => { setBusy(false); setError(errorText(err, "The import failed. No contacts were added.")); });
    };
    const header = rows && mapping && mapping.hasHeader ? rows[0] : (rows ? rows[0].map((_, i) => "Column "+(i + 1)) : []);
    const columnSelect = (key, label, optional) => (
        <label>
            <span className={classes.label}>{label}{optional ? " (optional)" : ""}</span>
            <select className={classes.input} value={mapping[key]} onChange={(e) => setMapping({ ...mapping, [key]: parseInt(e.target.value, 10) })}>
                {optional || key === "name" ? <option value={-1}>— None —</option> : null}
                {header.map((h, i) => <option key={i} value={i}>{h || "Column "+(i + 1)}</option>)}
            </select>
        </label>
    );

    return (<>
        <div className={classes.tabs} role="tablist">
            {[["csv", "Import from File (CSV)"], ["vcard", "Import from vCard"]].map(([key, label]) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} className={classes.tab+" "+(tab === key ? classes.tabActive : "")}
                    onClick={() => { setTab(key); reset(); }}>{label}</button>
            ))}
        </div>
        {error ? <div className={classes.error} role="alert">{error}</div> : null}
        {records.length === 0 ?
            <div className={classes.dropzone+" "+(drag ? classes.dropActive : "")}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); readFile(e.dataTransfer.files[0]); }}>
                <PublishOutlined aria-hidden="true" />
                <span>Drag and drop your {tab === "csv" ? "CSV" : "vCard"} file here, or</span>
                <button type="button" className={classes.secondary} onClick={() => inputRef.current && inputRef.current.click()}>Choose File</button>
                <input ref={inputRef} type="file" hidden accept={tab === "csv" ? ".csv,text/csv" : ".vcf,text/vcard,text/x-vcard"}
                    onChange={(e) => { readFile(e.target.files[0]); e.target.value = ""; }} />
                <span className={classes.muted}>{tab === "csv" ? "UTF-8 CSV with columns Name, Email, Group (optional)." : "vCard (.vcf) — names and email addresses are imported."}</span>
            </div>
        : <>
            <div className={classes.toolbar}>
                <strong>{fileName}</strong>
                <span className={classes.muted}>{records.length} rows · {counts.ok || 0} new · {counts.existing || 0} already contacts · {counts.duplicate || 0} duplicates · {counts.invalid || 0} invalid</span>
                <button type="button" className={classes.linkButton} onClick={reset}>Choose another file</button>
            </div>
            {tab === "csv" && mapping ?
                <div className={classes.formGrid}>
                    {columnSelect("email", "Email column")}{columnSelect("name", "Name column")}{columnSelect("group", "Group column", true)}
                    <label className={classes.groupPickItem} style={{ alignSelf: "end" }}>
                        <input type="checkbox" className={classes.checkbox} checked={mapping.hasHeader} onChange={(e) => setMapping({ ...mapping, hasHeader: e.target.checked })} />
                        First row is a header
                    </label>
                </div>
            : null}
            <div className={classes.tableWrap}>
                <table className={classes.table}>
                    <thead><tr><th scope="col">Row</th><th scope="col">Name</th><th scope="col">Email</th><th scope="col">Group</th><th scope="col">Status</th></tr></thead>
                    <tbody>{records.slice(0, 500).map((r, i) => (
                        <tr key={i}><td>{r.row}</td><td>{r.name}</td><td>{r.email}</td><td>{r.group}</td>
                            <td><span className={classes.status+" "+(r.status === "ok" ? classes.statusOk : r.status === "invalid" ? classes.statusBad : classes.statusSkip)}>
                                {r.status === "ok" ? "Will import" : r.message}</span></td></tr>
                    ))}</tbody>
                </table>
            </div>
            {records.length > 500 ? <p className={classes.muted}>Showing the first 500 rows.</p> : null}
            {hasGroups ?
                <label className={classes.groupPickItem}>
                    <input type="checkbox" className={classes.checkbox} checked={createGroups} onChange={(e) => setCreateGroups(e.target.checked)} />
                    Create groups that don't exist yet (otherwise group values that don't match an existing group are ignored)
                </label>
            : null}
            <p className={classes.muted}>Existing contacts are never overwritten. Duplicates and invalid rows are skipped.</p>
            <div className={classes.toolbar}>
                <button type="button" className={classes.secondary} onClick={reset} disabled={busy}>Cancel</button>
                <button type="button" className={classes.primary} onClick={confirm} disabled={busy || importable.length === 0}>
                    {busy ? "Importing…" : "Import "+importable.length+" contact"+(importable.length === 1 ? "" : "s")}
                </button>
            </div>
        </>}
    </>);
}

/* ------------------------------ Manage ------------------------------ */
function ManagePanel({ classes, contacts, groups, reload, onFavourite, onGroupFavourite, goImport }){
    const [tab, setTab] = useState("contacts");
    const [search, setSearch] = useState("");
    const [groupFilter, setGroupFilter] = useState("all");
    const [editing, setEditing] = useState(null);   // contact or group draft
    const [confirm, setConfirm] = useState(null);   // { type, item }
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [memberSearch, setMemberSearch] = useState("");

    const q = lower(search).trim();
    const contactRows = contacts.filter(c => (!q || lower(c.name).includes(q) || lower(c.email).includes(q) || lower(c.company).includes(q)) &&
        (groupFilter === "all" || c.groups.some(g => String(g.id) === String(groupFilter))));
    const groupRows = groups.filter(g => !q || lower(g.name).includes(q) || lower(g.description).includes(q));

    const run = (promise, after) => {
        setBusy(true);
        setError(null);
        return promise.then(() => reload()).then(() => { setBusy(false); after && after(); })
            .catch(err => { setBusy(false); setError(errorText(err, "The change could not be saved.")); });
    };
    const saveContact = () => {
        if(!isValidEmail(String(editing.email || "").trim())){ setError("Enter a valid email address."); return; }
        if(String(editing.firstName || "").trim().length > NAME_MAX || String(editing.lastName || "").trim().length > NAME_MAX){ setError("Names can be at most "+NAME_MAX+" characters."); return; }
        if(String(editing.company || "").trim().length > COMPANY_MAX){ setError("Company can be at most "+COMPANY_MAX+" characters."); return; }
        run(ContactsApi.saveContact(editing), () => setEditing(null));
    };
    const saveGroup = () => {
        if(!String(editing.name || "").trim()){ setError("Group name is required."); return; }
        run(ContactsApi.saveGroup(editing), () => setEditing(null));
    };

    if(editing && editing.kind === "contact"){
        return (<>
            <h3 className={classes.title}>{editing.id ? "Edit Contact" : "Add Contact"}</h3>
            {error ? <div className={classes.error} role="alert">{error}</div> : null}
            <div className={classes.formGrid}>
                <label><span className={classes.label}>First name</span>
                    <input className={classes.input} value={editing.firstName} maxLength={NAME_MAX} onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} autoFocus /></label>
                <label><span className={classes.label}>Last name</span>
                    <input className={classes.input} value={editing.lastName} maxLength={NAME_MAX} onChange={(e) => setEditing({ ...editing, lastName: e.target.value })} /></label>
                <label><span className={classes.label}>Company</span>
                    <input className={classes.input} value={editing.company} maxLength={COMPANY_MAX} onChange={(e) => setEditing({ ...editing, company: e.target.value })} /></label>
                <label><span className={classes.label}>Email *</span>
                    <input className={classes.input} type="email" value={editing.email} maxLength={255} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></label>
            </div>
            <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
                <legend className={classes.label}>Groups (optional)</legend>
                <div className={classes.groupPick}>
                    {groups.length === 0 ? <span className={classes.muted}>No groups yet.</span> : groups.map(g => (
                        <label key={g.id} className={classes.groupPickItem}>
                            <input type="checkbox" className={classes.checkbox} checked={editing.groupIds.includes(g.id)}
                                onChange={(e) => setEditing({ ...editing, groupIds: e.target.checked ? editing.groupIds.concat([g.id]) : editing.groupIds.filter(id => id !== g.id) })} />
                            {g.name}
                        </label>
                    ))}
                </div>
            </fieldset>
            <div className={classes.toolbar}>
                <button type="button" className={classes.secondary} onClick={() => { setEditing(null); setError(null); }} disabled={busy}>Cancel</button>
                <button type="button" className={classes.primary} onClick={saveContact} disabled={busy}>{busy ? "Saving…" : "Save Contact"}</button>
            </div>
        </>);
    }
    if(editing && editing.kind === "group"){
        const mq = lower(memberSearch).trim();
        const candidates = contacts.filter(c => !mq || lower(c.name).includes(mq) || lower(c.email).includes(mq));
        return (<>
            <h3 className={classes.title}>{editing.id ? "Edit Group" : "Create Group"}</h3>
            {error ? <div className={classes.error} role="alert">{error}</div> : null}
            <div className={classes.formGrid}>
                <label><span className={classes.label}>Group name *</span>
                    <input className={classes.input} value={editing.name} maxLength={120} placeholder="e.g. ABC Inc – Accounting" onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus /></label>
                <label><span className={classes.label}>Description</span>
                    <input className={classes.input} value={editing.description} maxLength={255} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></label>
            </div>
            <label className={classes.groupPickItem}>
                <input type="checkbox" className={classes.checkbox} checked={!!editing.favourite} onChange={(e) => setEditing({ ...editing, favourite: e.target.checked })} />
                Show this group under Favourites
            </label>
            <div className={classes.toolbar}>
                <input type="search" className={classes.search} placeholder="Search contacts to add…" aria-label="Search contacts to add" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />
                <span className={classes.count}>{editing.contactIds.length} member{editing.contactIds.length === 1 ? "" : "s"}</span>
            </div>
            <div className={classes.tableWrap} style={{ maxHeight: 240 }}>
                <table className={classes.table}>
                    <tbody>{candidates.length === 0 ? <tr><td className={classes.empty}>No contacts found. Add contacts first, then add them to this group.</td></tr> : candidates.map(c => (
                        <tr key={c.id}>
                            <td style={{ width: 44 }}><input type="checkbox" className={classes.checkbox} aria-label={"Add "+(c.name || c.email)} checked={editing.contactIds.includes(c.id)}
                                onChange={(e) => setEditing({ ...editing, contactIds: e.target.checked ? editing.contactIds.concat([c.id]) : editing.contactIds.filter(id => id !== c.id) })} /></td>
                            <td>{c.name}</td><td>{c.email}</td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>
            <div className={classes.toolbar}>
                <button type="button" className={classes.secondary} onClick={() => { setEditing(null); setError(null); }} disabled={busy}>Cancel</button>
                <button type="button" className={classes.primary} onClick={saveGroup} disabled={busy}>{busy ? "Saving…" : "Save Group"}</button>
            </div>
        </>);
    }
    if(confirm){
        const isGroup = confirm.type === "group";
        return (<>
            <h3 className={classes.title}>{isGroup ? "Delete group?" : "Delete contact?"}</h3>
            {error ? <div className={classes.error} role="alert">{error}</div> : null}
            <p>{isGroup ? <>The group <strong>{confirm.item.name}</strong> will be deleted. Its contacts are kept.</>
                : <><strong>{confirm.item.name || confirm.item.email}</strong> ({confirm.item.email}) will be removed from your contacts and from all groups.</>}</p>
            <div className={classes.toolbar}>
                <button type="button" className={classes.secondary} onClick={() => { setConfirm(null); setError(null); }} disabled={busy} autoFocus>Cancel</button>
                <button type="button" className={classes.danger} disabled={busy}
                    onClick={() => run(isGroup ? ContactsApi.deleteGroup(confirm.item.id) : ContactsApi.deleteContact(confirm.item.id), () => setConfirm(null))}>
                    {busy ? "Deleting…" : "Delete"}
                </button>
            </div>
        </>);
    }

    return (<>
        <div className={classes.tabs} role="tablist">
            {[["contacts", "Contacts ("+contacts.length+")"], ["groups", "Groups ("+groups.length+")"]].map(([key, label]) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} className={classes.tab+" "+(tab === key ? classes.tabActive : "")}
                    onClick={() => { setTab(key); setSearch(""); }}>{label}</button>
            ))}
        </div>
        {error ? <div className={classes.error} role="alert">{error}</div> : null}
        <div className={classes.toolbar}>
            <input type="search" className={classes.search} placeholder={tab === "groups" ? "Search groups…" : "Search contacts…"} aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {tab === "contacts" ?
                <select className={classes.select} aria-label="Group" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}>
                    <option value="all">All Groups</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
            : null}
            <button type="button" className={classes.secondary} onClick={goImport}>Import Contacts</button>
            <button type="button" className={classes.primary} onClick={() => tab === "groups"
                ? setEditing({ kind: "group", name: "", description: "", favourite: false, contactIds: [] })
                : setEditing({ kind: "contact", firstName: "", lastName: "", company: "", email: "", groupIds: [] })}>
                + {tab === "groups" ? "Create Group" : "Add Contact"}
            </button>
        </div>
        <div className={classes.tableWrap}>
            {tab === "contacts" ?
                <table className={classes.table}>
                    <thead><tr><th scope="col">Name</th><th scope="col">Email</th><th scope="col">Company</th><th scope="col">Groups</th><th scope="col"><span className={classes.srOnly}>Actions</span></th></tr></thead>
                    <tbody>{contactRows.length === 0 ? <tr><td colSpan={5} className={classes.empty}>No contacts found.</td></tr> : contactRows.map(c => (
                        <tr key={c.id}>
                            <td>{c.name || <span className={classes.muted}>—</span>}</td><td>{c.email}</td><td>{c.company || <span className={classes.muted}>—</span>}</td>
                            <td>{c.groups.map(g => g.name).join(", ") || <span className={classes.muted}>—</span>}</td>
                            <td><div className={classes.actions}>
                                <button type="button" className={classes.iconButton+" "+(c.favourite ? classes.starOn : "")} aria-pressed={c.favourite} aria-label={"Favourite "+(c.name || c.email)} onClick={() => onFavourite(c)}>{c.favourite ? <Star /> : <StarBorder />}</button>
                                <button type="button" className={classes.iconButton} aria-label={"Edit "+(c.name || c.email)}
                                    onClick={() => setEditing({ kind: "contact", id: c.id, email: c.email, groupIds: c.groups.map(g => g.id), company: c.company || "",
                                        // Contacts saved before First/Last existed keep their whole name in First name.
                                        firstName: c.firstName || (!c.lastName ? c.name || "" : ""), lastName: c.lastName || "" })}><EditOutlined /></button>
                                <button type="button" className={classes.iconButton} aria-label={"Delete "+(c.name || c.email)} onClick={() => setConfirm({ type: "contact", item: c })}><DeleteOutline /></button>
                            </div></td>
                        </tr>
                    ))}</tbody>
                </table>
            :
                <table className={classes.table}>
                    <thead><tr><th scope="col">Group</th><th scope="col">Description</th><th scope="col">Members</th><th scope="col"><span className={classes.srOnly}>Actions</span></th></tr></thead>
                    <tbody>{groupRows.length === 0 ? <tr><td colSpan={4} className={classes.empty}>No groups yet. Create one to reuse a set of contacts.</td></tr> : groupRows.map(g => (
                        <tr key={g.id}>
                            <td><strong>{g.name}</strong></td><td>{g.description || <span className={classes.muted}>—</span>}</td><td>{g.memberCount}</td>
                            <td><div className={classes.actions}>
                                <button type="button" className={classes.iconButton+" "+(g.favourite ? classes.starOn : "")} aria-pressed={g.favourite} aria-label={"Favourite "+g.name} onClick={() => onGroupFavourite(g)}>{g.favourite ? <Star /> : <StarBorder />}</button>
                                <button type="button" className={classes.iconButton} aria-label={"Edit "+g.name}
                                    onClick={() => setEditing({ kind: "group", id: g.id, name: g.name, description: g.description, favourite: g.favourite, contactIds: g.memberIds.slice() })}><EditOutlined /></button>
                                <button type="button" className={classes.iconButton} aria-label={"Delete "+g.name} onClick={() => setConfirm({ type: "group", item: g })}><DeleteOutline /></button>
                            </div></td>
                        </tr>
                    ))}</tbody>
                </table>
            }
        </div>
    </>);
}

AddressBookModal.defaultProps = { open: false, existingRecipients: [], initialView: "contacts", user: null, onOpenExtensions: null };
AddressBookModal.propTypes = {
    classes: PropTypes.object.isRequired,
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    existingRecipients: PropTypes.array,
    initialView: PropTypes.string,
    // Signed-in user (store user): decides whether the third-party address books are shown.
    user: PropTypes.object,
    // Optional: navigate to Extensions with the app router (default: full page navigation).
    onOpenExtensions: PropTypes.func
};

export default withStyles(addressBookStyle)(AddressBookModal);
